import React, { createContext, useContext, useState } from 'react';

export interface Property {
  name: string;
  value: unknown; // We don't know the type of value in advance
}
export interface Content {
  type: string;
  input?: {
    content: {
      'application/json': {
        properties: Property[];
      };
    };
  };
}

export interface ChatMessage {
  id: number;
  text?: string;
  content?: Content; // New field to store the JSON object for MutationMessage
  isUser: boolean;
  choices?: string[];
  onChoiceSelect?: (choice: string) => void;
  agentExecution?: boolean;
  userInput?: string;
  mutationMessage?: boolean;
  type?: 'text' | 'agentExecution' | 'mutation' | 'queryExplorer'; // Add 'queryExplorer' type
}

interface MessagesContextValue {
  messages: ChatMessage[];
  addMessage: (message: ChatMessage) => void;
  clearMessages: () => void; // Method to clear all messages
  setAgentExecuting: (isExecuting: boolean) => void;
  agentExecuting: boolean;
}

const MessagesContext = createContext<MessagesContextValue | undefined>(undefined);

export const MessagesProvider = ({ children }: { children: React.ReactNode }) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [agentExecuting, setAgentExecuting] = useState<boolean>(false);

  const addMessage = (message: ChatMessage) => {
    setMessages((prevMessages) => [...prevMessages, message]);
  };

  const clearMessages = () => {
    setMessages([]); // Clear all messages by setting the state to an empty array
  };

  return (
    <MessagesContext.Provider
      value={{ messages, addMessage, clearMessages, setAgentExecuting, agentExecuting }}
    >
      {children}
    </MessagesContext.Provider>
  );
};

export const useMessagesContext = () => {
  const context = useContext(MessagesContext);
  if (!context) {
    throw new Error('useMessagesContext must be used within a MessagesProvider');
  }
  return context;
};
